import React from 'react';
import toast from 'react-hot-toast';
import {
  buildNotificationNavigationPayload,
  performNotificationClickNavigation,
} from './notificationNavigation';

/**
 * Shared look for FCM + WebSocket realtime alerts (gradient, bell, top-right).
 * Matches the in-app “Ticket Status Updated” style.
 */
export const REALTIME_NOTIFICATION_TOAST_OPTIONS = {
  duration: 6000,
  position: 'top-right',
  style: {
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
    maxWidth: '400px',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '1.5',
  },
  icon: '🔔',
  iconTheme: {
    primary: '#fff',
    secondary: '#667eea',
  },
};

const DEDUPE_MS = 5000;
/** @type {Map<string, number>} */
const recentToastKeys = new Map();

const LOG_NS = 'realtime-toast-visibility';

const MAX_PENDING = 15;
const MAX_FLUSH_PER_VISIBLE = 5;
const FLUSH_STAGGER_MS = 450;
const MAX_PENDING_AGE_MS = 30 * 60 * 1000;

/** @type {{ title: string, body: string, options: object, dedupeKey: string, msg: string, queuedAt: number, notification: object | null }[]} */
const pendingToastQueue = [];

let visibilityListenerAttached = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let flushStaggerRootTimer = null;

function logVisibility(stage, fields = {}) {
  //console.log(
  //   JSON.stringify({
  //     ns: LOG_NS,
  //     stage,
  //     ts: new Date().toISOString(),
  //     ...fields,
  //   })
  // );
}

function isDocumentVisible() {
  return typeof document !== 'undefined' && document.visibilityState === 'visible';
}

function cleanupStaleDedupeKeys(now) {
  for (const [k, t] of recentToastKeys.entries()) {
    if (now - t > DEDUPE_MS * 2) {
      recentToastKeys.delete(k);
    }
  }
}

/** True if this key was shown (or reserved) recently — do not queue/show duplicate. */
function isDuplicateToastKey(dedupeKey, now = Date.now()) {
  cleanupStaleDedupeKeys(now);
  const last = recentToastKeys.get(dedupeKey);
  return last != null && now - last < DEDUPE_MS;
}

function recordToastDedupe(dedupeKey) {
  recentToastKeys.set(dedupeKey, Date.now());
}

/**
 * @param {string} dedupeKey
 * @returns {boolean} true = skip (duplicate)
 */
function shouldSkipDuplicateToast(dedupeKey) {
  const now = Date.now();
  if (isDuplicateToastKey(dedupeKey, now)) {
    return true;
  }
  recordToastDedupe(dedupeKey);
  return false;
}

function buildDedupeKey(title, body, options) {
  const line1 = title || 'Notification';
  const line2 = body != null ? String(body).trim() : '';
  return options.dedupeKey != null && String(options.dedupeKey).trim() !== ''
    ? `id:${String(options.dedupeKey).trim()}`
    : `content:${line1}|${line2}`;
}

function buildToastMessage(title, body) {
  const line1 = title || 'Notification';
  const line2 = body != null ? String(body).trim() : '';
  return line2 ? `${line1}\n${line2}` : line1;
}

function resolveToastNavigationPayload(options) {
  if (options?.notification && typeof options.notification === 'object') {
    return buildNotificationNavigationPayload(options.notification);
  }
  return null;
}

function handleToastClick(t, navigationPayload) {
  if (navigationPayload) {
    performNotificationClickNavigation(navigationPayload);
  }
  toast.dismiss(t.id);
}

function RealtimeNotificationToastContent({ t, title, body, navigationPayload }) {
  const line1 = title || 'Notification';
  const line2 = body != null ? String(body).trim() : '';
  const clickable = Boolean(navigationPayload);

  return (
    <div
      role={clickable ? 'button' : 'status'}
      tabIndex={clickable ? 0 : undefined}
      onClick={() => {
        if (clickable) {
          handleToastClick(t, navigationPayload);
        }
      }}
      onKeyDown={(e) => {
        if (!clickable) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToastClick(t, navigationPayload);
        }
      }}
      style={{
        ...REALTIME_NOTIFICATION_TOAST_OPTIONS.style,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        cursor: clickable ? 'pointer' : 'default',
        width: '100%',
        boxSizing: 'border-box',
      }}
      title={clickable ? 'Click to open' : undefined}
    >
      <span style={{ fontSize: '18px', lineHeight: 1.2, flexShrink: 0 }} aria-hidden>
        🔔
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{line1}</div>
        {line2 ? (
          <div style={{ marginTop: 4, opacity: 0.95, whiteSpace: 'pre-wrap' }}>{line2}</div>
        ) : null}
      </div>
    </div>
  );
}

function removeQueuedEntriesWithKey(dedupeKey) {
  let removed = 0;
  for (let i = pendingToastQueue.length - 1; i >= 0; i--) {
    if (pendingToastQueue[i].dedupeKey === dedupeKey) {
      pendingToastQueue.splice(i, 1);
      removed += 1;
    }
  }
  if (removed > 0) {
    logVisibility('toast_queue_coalesced', { dedupeKey, removed });
  }
}

function enqueuePendingToast(title, body, options, dedupeKey, msg, navigationPayload) {
  const now = Date.now();
  if (isDuplicateToastKey(dedupeKey, now)) {
    logVisibility('toast_deferred_hidden_tab', {
      reason: 'duplicate_within_dedupe_window',
      dedupeKey,
    });
    return;
  }

  removeQueuedEntriesWithKey(dedupeKey);
  pendingToastQueue.push({
    title,
    body,
    options,
    dedupeKey,
    msg,
    queuedAt: now,
    notification: navigationPayload,
  });

  while (pendingToastQueue.length > MAX_PENDING) {
    pendingToastQueue.shift();
    logVisibility('toast_deferred_hidden_tab', { reason: 'queue_cap_evict', maxPending: MAX_PENDING });
  }

  logVisibility('toast_queued_hidden_tab', {
    dedupeKey,
    queueLength: pendingToastQueue.length,
  });
}

function performToastShow(title, body, navigationPayload) {
  toast.custom(
    (t) => (
      <RealtimeNotificationToastContent
        t={t}
        title={title}
        body={body}
        navigationPayload={navigationPayload}
      />
    ),
    {
      duration: REALTIME_NOTIFICATION_TOAST_OPTIONS.duration,
      position: REALTIME_NOTIFICATION_TOAST_OPTIONS.position,
    }
  );
}

function flushPendingToastsOnVisible() {
  if (flushStaggerRootTimer != null) {
    clearTimeout(flushStaggerRootTimer);
    flushStaggerRootTimer = null;
  }

  if (pendingToastQueue.length === 0) {
    return;
  }

  const snapshot = pendingToastQueue.splice(0, pendingToastQueue.length);
  const now = Date.now();
  const fresh = [];
  let staleDropped = 0;
  for (const item of snapshot) {
    if (now - item.queuedAt > MAX_PENDING_AGE_MS) {
      staleDropped += 1;
      logVisibility('toast_flush_skipped_duplicate', {
        reason: 'stale_pending_age',
        dedupeKey: item.dedupeKey,
        ageMs: now - item.queuedAt,
      });
      continue;
    }
    fresh.push(item);
  }

  const toFlush = fresh.slice(0, MAX_FLUSH_PER_VISIBLE);
  const droppedOverCap = fresh.length - toFlush.length;

  logVisibility('toast_flush_on_visible', {
    pendingTaken: snapshot.length,
    afterStaleFilter: fresh.length,
    staleDropped,
    willShow: toFlush.length,
    droppedOverCap,
    maxFlush: MAX_FLUSH_PER_VISIBLE,
    staggerMs: FLUSH_STAGGER_MS,
  });

  toFlush.forEach((item, idx) => {
    const delay = idx * FLUSH_STAGGER_MS;
    setTimeout(() => {
      if (!isDocumentVisible()) {
        removeQueuedEntriesWithKey(item.dedupeKey);
        pendingToastQueue.unshift(item);
        while (pendingToastQueue.length > MAX_PENDING) {
          pendingToastQueue.pop();
          logVisibility('toast_deferred_hidden_tab', { reason: 'queue_cap_evict_mid_flush' });
        }
        logVisibility('toast_queued_hidden_tab', {
          reason: 'tab_hidden_mid_flush',
          dedupeKey: item.dedupeKey,
          queueLength: pendingToastQueue.length,
        });
        return;
      }
      if (shouldSkipDuplicateToast(item.dedupeKey)) {
        logVisibility('toast_flush_skipped_duplicate', {
          phase: 'flush_dedupe',
          dedupeKey: item.dedupeKey,
        });
        return;
      }
      performToastShow(item.title, item.body, item.notification);
    }, delay);
  });

  const completionDelay = Math.max(0, (toFlush.length - 1) * FLUSH_STAGGER_MS) + 80;
  flushStaggerRootTimer = setTimeout(() => {
    flushStaggerRootTimer = null;
    logVisibility('toast_flush_completed', {
      scheduled: toFlush.length,
      droppedOverCap,
      staleDropped,
      note: 'per-item dedupe and mid-tab-hide re-queue logged separately',
    });
  }, completionDelay);
}

function onDocumentVisibilityChange() {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.visibilityState !== 'visible') {
    return;
  }
  flushPendingToastsOnVisible();
}

function ensureVisibilityListener() {
  if (typeof document === 'undefined' || visibilityListenerAttached) {
    return;
  }
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', onDocumentVisibilityChange);
}

/**
 * Clear queued toasts (e.g. logout). Does not dismiss on-screen react-hot-toast instances.
 */
export function clearRealtimeNotificationToastQueue() {
  const n = pendingToastQueue.length;
  pendingToastQueue.length = 0;
  if (flushStaggerRootTimer != null) {
    clearTimeout(flushStaggerRootTimer);
    flushStaggerRootTimer = null;
  }
  if (n > 0) {
    logVisibility('toast_flush_completed', { reason: 'queue_cleared', evicted: n });
  }
}

/**
 * @param {string} title - Bold line (e.g. "Ticket Status Updated")
 * @param {string} [body] - Second line (e.g. ticket message)
 * @param {{ dedupeKey?: string, notification?: object }} [options] - Pass notification for bell-identical navigation on click
 */
export function showRealtimeNotificationToast(title, body, options = {}) {
  ensureVisibilityListener();

  const line1 = title || 'Notification';
  const line2 = body != null ? String(body).trim() : '';
  const msg = buildToastMessage(title, body);
  const dedupeKey = buildDedupeKey(title, body, options);
  const navigationPayload = resolveToastNavigationPayload(options);

  if (!isDocumentVisible()) {
    enqueuePendingToast(title, body, options, dedupeKey, msg, navigationPayload);
    return;
  }

  if (shouldSkipDuplicateToast(dedupeKey)) {
    return;
  }

  performToastShow(title, body, navigationPayload);
}
